import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";
import { Trash2, Keyboard, RotateCcw } from "lucide-react";

// Admin-only keyboard shortcuts config. Each row shows the shortcut label,
// an enable/disable toggle, editable Mac + Windows key bindings, and a
// Delete (disable + reset to blank) button.
export default function KeyboardShortcutsPanel() {
  const [catalog, setCatalog] = useState({});
  const [state, setState] = useState({});
  const [dirty, setDirty] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/site-settings/keyboard-shortcuts").then(r => {
      setCatalog(r.data.catalog || {});
      setState(r.data.shortcuts || {});
    });
  }, []);

  const set = (sid, patch) => {
    setState(prev => ({ ...prev, [sid]: { ...prev[sid], ...patch } }));
    setDirty(true);
  };
  const reset = (sid) => {
    const c = catalog[sid];
    set(sid, { enabled: true, mac: c.default_mac, win: c.default_win });
  };
  const del = (sid) => {
    // "Deleting" a shortcut disables it and blanks its key bindings — a hard
    // remove would require code-side handling in every component; disabling
    // is the practical equivalent.
    set(sid, { enabled: false, mac: "", win: "" });
    toast(`"${catalog[sid]?.label}" disabled`, {
      action: { label: "Undo", onClick: () => reset(sid) },
    });
  };

  const save = async () => {
    setSaving(true);
    try {
      const r = await api.put("/site-settings/keyboard-shortcuts", state);
      setState(r.data.shortcuts);
      setDirty(false);
      toast.success("Shortcuts saved");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Save failed");
    } finally { setSaving(false); }
  };

  const entries = Object.entries(catalog);
  return (
    <div data-testid="keyboard-shortcuts-panel" className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <div className="font-heading text-xl font-semibold text-slate-900 inline-flex items-center gap-2">
            <Keyboard className="w-5 h-5"/>Keyboard shortcuts
          </div>
          <div className="text-sm text-slate-500">Admin-only keyboard shortcuts. Configure Mac + Windows bindings separately, or delete a shortcut to disable it.</div>
        </div>
        <Button data-testid="ks-save" onClick={save} disabled={saving || !dirty} className="bg-sky-700 hover:bg-sky-600">
          {saving ? "Saving…" : dirty ? "Save changes" : "Saved"}
        </Button>
      </div>

      <div className="border border-slate-200 rounded-lg overflow-hidden bg-white">
        <div className="grid grid-cols-[minmax(0,1fr)_90px_160px_160px_60px] gap-3 px-4 py-2 text-[10px] uppercase tracking-widest text-slate-500 bg-slate-50 border-b border-slate-200">
          <div>Shortcut</div>
          <div>Enabled</div>
          <div>Mac keys</div>
          <div>Windows keys</div>
          <div className="text-right">Delete</div>
        </div>
        {entries.length === 0 && <div className="p-4 text-sm text-slate-500 italic">No shortcuts defined.</div>}
        {entries.map(([sid, meta]) => {
          const cell = state[sid] || { enabled: true, mac: meta.default_mac, win: meta.default_win };
          const isDefault = cell.mac === meta.default_mac && cell.win === meta.default_win && cell.enabled;
          return (
            <div key={sid} data-testid={`ks-row-${sid}`}
                 className="grid grid-cols-[minmax(0,1fr)_90px_160px_160px_60px] gap-3 px-4 py-3 items-center border-b border-slate-100 last:border-b-0">
              <div className="min-w-0">
                <div className="text-sm font-medium text-slate-900 truncate">{meta.label}</div>
                <div className="text-[11px] text-slate-500 uppercase tracking-widest">{meta.scope}</div>
              </div>
              <Switch data-testid={`ks-toggle-${sid}`}
                      checked={cell.enabled}
                      onCheckedChange={v => set(sid, { enabled: v })}/>
              <Input data-testid={`ks-mac-${sid}`} value={cell.mac || ""}
                     onChange={e => set(sid, { mac: e.target.value })}
                     placeholder={meta.default_mac}
                     className="h-8 text-xs font-mono"/>
              <Input data-testid={`ks-win-${sid}`} value={cell.win || ""}
                     onChange={e => set(sid, { win: e.target.value })}
                     placeholder={meta.default_win}
                     className="h-8 text-xs font-mono"/>
              <div className="flex justify-end gap-1">
                {!isDefault && (
                  <button onClick={() => reset(sid)}
                          title="Reset to default"
                          className="text-slate-400 hover:text-sky-700 p-1 rounded">
                    <RotateCcw className="w-3.5 h-3.5"/>
                  </button>
                )}
                <button data-testid={`ks-delete-${sid}`} onClick={() => del(sid)}
                        title="Delete (disable)"
                        className="text-slate-400 hover:text-red-600 p-1 rounded">
                  <Trash2 className="w-4 h-4"/>
                </button>
              </div>
            </div>
          );
        })}
      </div>

      <div className="text-xs text-slate-500">
        <strong>Syntax:</strong> combine modifiers with <code className="bg-slate-100 px-1 rounded">+</code>. Supported modifiers: <code>Ctrl</code>, <code>Cmd</code>, <code>Alt</code> (or <code>Option</code>), <code>Shift</code>. Then the key — <code>Z</code>, <code>D</code>, etc. Example: <code>Ctrl+Alt+D</code>.
      </div>
    </div>
  );
}
